import * as React from 'react'
import { ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import HeaderComponent from '@components/header/Header'
import ConformityCard from '@components/card/ConformityCard'
import Icon from '@components/icon/Icons'
import Button from '@components/button/Button'
import SimpleAlert from '@components/modal/SimpleAlert'

import api from '@api/api'
import logger from '@utils/logger'

import useReducer from '@store/useReducer'
import * as AuthStore from '@store/auth/auth'
import * as ControlesStore from '@store/controle/controle'
import * as SocieteStore from '@store/societe/societe'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as LocationStore from '@store/location/location'
import * as QuestionStore from '@store/question/question'
import * as UsersStore from '@store/users/users'
import * as MetierStore from '@store/metier/metier'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import { filterInTree, scoreToRate, TEXT_COLORS } from '@screens/controle/form/utils'
import { paramsToSearch } from '@api/utils'
import { AuthScope } from '@api/enum'
import { setResults, ResultStore } from './ControleHistoryListScreen'

interface Props {
  controle: ControleQualiteResponse
  buttons: HeaderButton[]

  // origin
  manifestationId?: string
  societeId?: string
  espaceId?: string
}

const Recap = ({ controle, buttons, manifestationId, societeId, espaceId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [, bp] = useBreakpoint()

  const [opens, setOpens] = React.useState<string[]>([])
  const [creator, setCreator] = React.useState<UserBasicResponse>()

  const questions = useReducer(QuestionStore.store, (s) => s.controle)
  const services = useReducer(SocieteStore.store, (s) => s.services)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const user = useReducer(UserStore.store, (s) => s.user)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const places = useReducer(LocationStore.store, (s) => s.places)
  const locationTree = useReducer(LocationStore.store, (s) => s.tree)
  const metiers = useReducer(MetierStore.store, (s) => s.metiers)
  const users = useReducer(UsersStore.store, (s) => s.users)

  React.useEffect(() => {
    const found = users.find((u) => u.id === controle.utilisateurId)
    if (found) {
      setCreator(found)
    } else {
      api.users.get(controle.utilisateurId).then(setCreator).catch(logger.error)
    }
  }, [controle])

  const questionIds = React.useMemo(() => questions.map((q) => q.id), [questions])

  const controleLocalisations = Array.from(new Set(controle.reponses.map((r) => r.localisationId)))

  const selectedLocationTree = React.useMemo(
    () => (controle ? locationTree.filter((item) => filterInTree(item, controleLocalisations)) : []),
    [locationTree, controle]
  )

  const controleSociete = React.useMemo(() => services.find((s) => s.id === controle.societeId), [services, controle])

  const metier = React.useMemo(() => metiers.find((m) => m.id === controle.metierId), [metiers])

  const controleManif = React.useMemo(
    () =>
      [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed].find(
        (m) => m.id === controle.manifestationId
      ),
    [manifestations, controle]
  )

  const controleScore = controle.tauxConformite
  const controleRate = scoreToRate(controleScore)

  // origin
  const societe = React.useMemo(() => societeId && services.find((s) => s.id === societeId), [services, societeId])
  const espace = React.useMemo(() => espaceId && places.find((s) => s.id === espaceId), [places, espaceId])
  const manifestation = React.useMemo(
    () =>
      manifestationId &&
      [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed].find(
        (s) => s.id === manifestationId
      ),
    [manifestations, manifestationId]
  )

  const headerLinks = [
    { label: i18n.t('screens.controles.title'), onClick: () => navigation.push('/controles') },
    societe
      ? {
          label: societe.nameSociete,
          onClick: () => navigation.jumpTo(`/controles/details/infos?societeId=${societeId}`),
        }
      : undefined,
    espace
      ? { label: espace.libelle, onClick: () => navigation.jumpTo(`/controles/details/infos?spaceId=${espaceId}`) }
      : undefined,
    manifestation
      ? {
          label: manifestation.libelle,
          onClick: () => navigation.jumpTo(`/controles/details/infos?manifestationId=${manifestationId}`),
        }
      : undefined,
    {
      label: i18n.t('screens.controles.history.title'),
      onClick: () =>
        navigation.jumpTo(`/controles/history/list${paramsToSearch({ manifestationId, societeId, espaceId })}`),
    },
    { label: `${controle.numero}`, onClick: () => null },
  ].filter(Boolean) as HeaderLink[]

  const onOpen = (id: string, open: boolean) => {
    if (open) {
      setOpens(opens.filter((o) => o !== id))
    } else {
      setOpens([...opens, id])
    }
  }

  if (!user) {
    return null
  }

  const renderResponse = (response: ControleResponseForm, idx: number, items: ControleResponseForm[]) => {
    const question = questions.find((q) => q.id === response.questionId)

    if (!question) {
      return null
    }

    return (
      <QuestionItem key={response.questionId + response.localisationId} last={idx === items.length - 1}>
        <QuestionInfo>{i18n.t('screens.controles.form.recap.question', { count: idx + 1 })}</QuestionInfo>
        <Line>
          <QuestionIcon>
            <Icon
              name={
                response.conformite === 'CONFORME' || response.conformite === 'NON_CONCERNE' ? 'checked' : 'problem'
              }
              size={20}
              color={theme.colors.primary}
            />
          </QuestionIcon>
          <Infos>
            <Info>{question.libelle}</Info>
            <QuestionInfo color={TEXT_COLORS[response.conformite]}>
              {i18n.t(`screens.controles.form.questions.conformite.${response.conformite}`)}
            </QuestionInfo>
          </Infos>
        </Line>

        {!!response.commentaire && (
          <CommentContainer>
            <Info>{response.commentaire}</Info>
          </CommentContainer>
        )}
      </QuestionItem>
    )
  }

  const renderLocation = (item: LocationTreeItem, index: number, list: LocationTreeItem[]) => {
    const audited = controleLocalisations.includes(item.location.id)
    const responses = controle.reponses
      .filter((r) => r.localisationId === item.location.id)
      .sort(
        (a, b) =>
          questionIds.findIndex((id) => id === a.questionId) - questionIds.findIndex((id) => id === b.questionId)
      )

    const open = opens.includes(item.location.id)
    const subs = item.subs.filter((sub) => filterInTree(sub, controleLocalisations))

    return (
      <React.Fragment key={item.location.id}>
        <ItemContent audited={audited && open} isLast={index === list.length - 1} open={open}>
          <ItemHeader activeOpacity={0.9} onPress={() => onOpen(item.location.id, open)}>
            <ItemOpen>
              <ItemText>{item.location.libelle}</ItemText>
            </ItemOpen>

            <IconContainer>
              <Icon name={open ? 'arrow-up' : 'arrow-down'} size={20} color={theme.colors.primary} />
            </IconContainer>
          </ItemHeader>
        </ItemContent>
        {!!open && <Subs>{audited ? responses.map(renderResponse) : subs.map(renderLocation)}</Subs>}
      </React.Fragment>
    )
  }

  const deleteControle = () => {
    const controleId = controle.id

    api.controle
      .delete(controleId)
      .then(() => {
        ControlesStore.actions.deleteControle(controleId)
        setResults(ResultStore.getState().filter((c) => c.id !== controleId))
        navigation.goBack()
      })
      .catch((err) => {
        logger.error(err)
        SimpleAlert.open({
          title: i18n.t('common.errorTitle'),
          description: i18n.t('common.error'),
          buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
        })
      })
  }

  const onDeleteControle = () => {
    SimpleAlert.open({
      title: i18n.t('screens.controle.delete'),
      description: i18n.t('screens.controle.deleteConfirm'),
      buttons: [
        { label: i18n.t('common.cancel'), onClick: () => SimpleAlert.close(), color: 'cancel' },
        {
          label: i18n.t('common.confirm'),
          onClick: () => {
            SimpleAlert.close()
            deleteControle()
          },
        },
      ],
    })
  }

  const renderHeader = () => {
    if (bp === 'phone') {
      return (
        <Header>
          <CreationDate>
            {i18n.t('screens.controle.creation', {
              date: new Date(controle.dateValidation),
              creator: creator ? `${creator.prenom} ${creator.nom}` : i18n.t('common.unknownUser'),
            })}
          </CreationDate>
          <Card>
            {!!controleSociete && (
              <Title center>{`${controleSociete.nameSociete}${!!metier ? ` - ${metier.libelle}` : ''}`}</Title>
            )}
            <SubTitle center>{`${i18n.t(`screens.controles.form.nature.${controle.nature}`)}${
              !!controleManif ? ` - ${controleManif.libelle}` : ''
            }${!!controle.period ? ` - ${i18n.t(`screens.controles.form.period.${controle.period}`)}` : ''}`}</SubTitle>

            <Separator />

            <Line>
              <ConformityContainer>
                <ConformityCard score={controleScore} />
              </ConformityContainer>
              <Infos>
                <ConformityContainer>
                  <Info>{i18n.t(`screens.controles.form.recap.${user.typeUtilisateur}`)}</Info>
                </ConformityContainer>
                <ConformityContainer>
                  <InfoBold color={TEXT_COLORS[controleRate.key]}>
                    {i18n.t(`screens.controles.form.questions.conformite.${controleRate.key}`)}
                  </InfoBold>
                </ConformityContainer>
              </Infos>
            </Line>
          </Card>
        </Header>
      )
    }

    return (
      <>
        <HeaderComponent
          title={i18n.t('screens.controle.title', { numero: controle.numero })}
          buttons={buttons}
          links={headerLinks}
          description={i18n.t('screens.controle.creation', {
            date: new Date(controle.dateValidation),
            creator: creator ? `${creator.prenom} ${creator.nom}` : i18n.t('common.unknownUser'),
          })}
        />

        <Header>
          <TitleContainer>
            <Titles>
              {!!controleSociete && (
                <Title>{`${controleSociete.nameSociete}${!!metier ? ` - ${metier.libelle}` : ''}`}</Title>
              )}
              <SubTitle>{`${i18n.t(`screens.controles.form.nature.${controle.nature}`)}${
                !!controleManif ? ` - ${controleManif.libelle}` : ''
              }${
                !!controle.period ? ` - ${i18n.t(`screens.controles.form.period.${controle.period}`)}` : ''
              }`}</SubTitle>
            </Titles>

            <ConformityContainer>
              <ConformityCard score={controleScore} />
            </ConformityContainer>
            <ConformityContainer>
              <Info>{i18n.t(`screens.controles.form.recap.${user.typeUtilisateur}`)}</Info>
            </ConformityContainer>
            <ConformityContainer>
              <InfoBold color={TEXT_COLORS[controleRate.key]}>
                {i18n.t(`screens.controles.form.questions.conformite.${controleRate.key}`)}
              </InfoBold>
            </ConformityContainer>
          </TitleContainer>
        </Header>
      </>
    )
  }

  return (
    <MainContainer>
      {renderHeader()}
      <Content>
        {!!controle.commentaire && (
          <Card>
            <InfoBold>{i18n.t('screens.controles.form.recap.commentaire')}</InfoBold>
            <CommentContainer>
              <Info>{controle.commentaire}</Info>
            </CommentContainer>
          </Card>
        )}

        <Card>
          <InfoBold>{i18n.t('screens.controles.form.recap.questions')}</InfoBold>
          <Locations>{selectedLocationTree.map(renderLocation)}</Locations>
        </Card>
      </Content>
      {scopes.includes(AuthScope.ControleQualiteSiteSuppression) && (
        <ButtonContainer>
          <Button color="cancel" label={i18n.t('common.delete')} status="active" onClick={onDeleteControle} />
        </ButtonContainer>
      )}
    </MainContainer>
  )
}

export default Recap

const MainContainer = styled(ScrollView)`
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  flex: 1;
  padding-bottom: 24px;
`

const Header = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '4px 0px 44px;' : '16px 0px')};
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.primary : props.theme.colors.cardSecondary};
  margin-bottom: 16px;
`

const Content = styled(View)`
  ${(props) => props.theme.breakpoint === 'phone' && `margin-top: -54px;`}
`

const Card = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
  border-radius: 8px;
  margin: 8px 24px;
`

const TitleContainer = styled(Card)`
  flex-direction: ${(props) => (props.theme.breakpoint !== 'big' ? 'column' : 'row')};
  align-items: center;
`

const Titles = styled(View)`
  ${(props) => (props.theme.breakpoint !== 'big' ? 'margin-bottom: 15px' : 'flex: 1')};
`

const Title = styled(Text)<{ center?: boolean }>`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Headline : props.theme.fonts.Title3)};
  ${(props) => (props.center || props.theme.breakpoint !== 'big') && 'text-align: center'};
`

const SubTitle = styled(Text)<{ center?: boolean }>`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) => (props.center || props.theme.breakpoint !== 'big') && 'text-align: center'};
`

const ConformityContainer = styled(View)`
  margin: ${(props) => (props.theme.breakpoint === 'big' ? 0 : 2)}px
    ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 20)}px;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const InfoBold = styled(Text)<{ color?: string }>`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.color || props.theme.colors.primaryText};
`

const CommentContainer = styled(View)`
  margin: 8px 0;
  padding: 16px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const Subs = styled(View)`
  margin-left: 16px;
`

const ItemHeader = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
`

const ItemContent = styled(View)<{ audited: boolean; isLast: boolean; open: boolean }>`
  border: 0 solid ${(props) => props.theme.colors.backgroundTertiary};
  border-bottom-width: ${(props) => (props.audited || (props.isLast && !props.open) ? 0 : 1)}px;
`

const ItemOpen = styled(View)`
  flex: 1;
  padding-left: 16px;
`

const IconContainer = styled(View)`
  width: 30px;
  align-items: center;
  justify-content: center;
`

const ItemText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const QuestionItem = styled(View)<{ last: boolean }>`
  border: 0 solid ${(props) => props.theme.colors.backgroundTertiary};
  border-bottom-width: ${(props) => (props.last ? 0 : 1)}px;
  padding-bottom: 8px;
  margin-bottom: 16px;
`

const QuestionInfo = styled(Text)<{ color?: string }>`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.color || props.theme.colors.secondaryText};
`

const Infos = styled(View)`
  flex: 1;
`

const Line = styled(View)`
  flex-direction: row;
  margin-top: 16px;
`

const QuestionIcon = styled(View)`
  padding: 0 10px;
`

const Separator = styled(View)`
  background-color: ${(props) => props.theme.colors.backgroundTertiary};
  height: 1px;
  margin-top: 16px;
`

const CreationDate = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  padding: 8px 16px;
`

const Locations = styled(View)`
  margin: 8px 0 -${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
`

const ButtonContainer = styled(View)`
  padding: 16px 24px;
  align-self: center;
`
